import { Icon } from '@buggy/shared'
import { Modal, notification } from 'antd'
import { useCallback } from 'react'
import { useTheme } from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useBatchUnlockKolsMutation } from '@/api/workspace-api/kol-api'
import { useFetchQuotaWalletUsageLazyQuery } from '@/api/workspaces-api'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PredictiveReportCandidate } from '@/types/schema/predictive-report'
import { ampli } from '@/utils/ampli'
import logger from '@/utils/logger'

type UseUnlockCandidateWithModal = () => (
  payload: PredictiveReportCandidate['uuid'][],
) => Promise<boolean>

const useUnlockCandidateWithModal: UseUnlockCandidateWithModal = () => {
  const { formatMessage } = useIntl()
  const [batchUnlockKols] = useBatchUnlockKolsMutation()
  const [fetchQuotaWalletUsage] = useFetchQuotaWalletUsageLazyQuery()
  const { data: userStatus } = useFetchUserStatusQuery()
  const theme = useTheme()

  return useCallback(
    async (payload) => {
      if (!userStatus) {
        return false
      }
      if (payload.length === 0) {
        return true
      }

      const [quotaWalletUsage, isUserWantToUnlockCandidate] = await Promise.all(
        [
          fetchQuotaWalletUsage({
            workspaceId: userStatus.currentWorkspaceId,
          }).unwrap(),
          new Promise((resolve) => {
            Modal.confirm({
              title: formatMessage({
                id: 'predictive_report:modal_title_unlock_candidate',
              }),
              content: formatMessage(
                {
                  id: 'predictive_report:modal_content_unlock_candidate',
                },
                { n: payload.length },
              ),
              okText: formatMessage({
                id: 'general:btn_text_unlock',
              }),
              cancelText: formatMessage({
                id: 'general:btn_text_skip',
              }),
              okButtonProps: {
                icon: <Icon name='lock' />,
                style: { background: theme.colors.plan.premium },
              },
              onOk: () => {
                resolve(true)
              },
              onCancel: () => {
                resolve(false)
              },
            })
          }),
        ],
      )

      if (!isUserWantToUnlockCandidate) {
        return false
      }

      const isQuotaEnough =
        (quotaWalletUsage.quota?.remainingQuota ?? 0) >= payload.length

      if (!isQuotaEnough) {
        Modal.confirm({
          title: formatMessage({
            id: 'predictive_report:modal_title_quota_insufficient',
          }),
          content: formatMessage({
            id: 'predictive_report:modal_content_quota_insufficient',
          }),
          okText: formatMessage({
            id: 'kol:btn_go_to_purchase',
          }),
          onOk: () => {
            routes.workspacePlan.goto({})
          },
          cancelText: formatMessage({
            id: 'general:btn_text_cancel',
          }),
        })
        return false
      }

      try {
        const isMultiple = payload.length > 1
        payload.forEach((candidateUuid) => {
          ampli.clickUnlockKolData({
            isMultiple,
            from: 'Proposal',
            kolId: candidateUuid,
          })
        })

        await batchUnlockKols({
          uuids: payload,
          from: 'Proposal',
        }).unwrap()
        notification.success({
          message: formatMessage({ id: 'kol:description_unlock_success' }),
          placement: 'bottomRight',
        })
      } catch (err) {
        Modal.confirm({
          title: formatMessage({
            id: 'predictive_report:modal_title_unlock_failure',
          }),
          content: formatMessage({
            id: 'predictive_report:modal_content_unlock_failure',
          }),
          okButtonProps: { style: { display: 'none' } },
          cancelText: formatMessage({
            id: 'general:go_back_act',
          }),
        })
        logger.error(err)
        return false
      }

      return true
    },
    [
      batchUnlockKols,
      fetchQuotaWalletUsage,
      formatMessage,
      theme.colors.plan.premium,
      userStatus,
    ],
  )
}

export default useUnlockCandidateWithModal
